import React, { useEffect, useState } from "react";
import { Route, Routes, Navigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import {
  MaintenanceProvider,
  MaintenanceGuard,
} from "./contexts/MaintenanceContext";
import "./styles/MaintenanceMode.css";
import { captureFacebookAdParams } from "./utils/adTracking";
import {
  FiFileText,
  FiLogOut,
  FiMenu,
  FiSettings,
  FiUser,
  FiChevronDown,
  FiChevronRight,
  FiMessageCircle, // Thêm import cho icon mới
  FiAward, // Thêm import FiAward
  FiShield, // Thêm icon cho Admin
  FiUsers, // Thêm icon cho quản lý người dùng
  FiActivity, // Thêm icon cho quản lý lỗi
  FiDatabase, // Thêm icon cho quản lý dữ liệu
  FiMessageSquare, // Icon for Feedback
  FiBookmark,
  FiSave, // Added FiBookmark import
} from "react-icons/fi"; // Add new icons
import NormalPostGenerator from "./NormalPostGenerator/NormalPostGenerator";
import Login from "./Login";
import { ref, onValue } from "firebase/database"; // Import Firebase database functions
import { getAuth } from "firebase/auth"; // Import Firebase Auth
import AutoPostGenerator from "./AutoPostGenerator";
import AutoPostConfigScreen from "./AutoPostConfigScreen";
import ProfileScreen from "./ProfileScreen";
import ProductCreateScreen from "./ProductCreateScreen"; // Import the new screen
import ProductsScreen from "./ProductsScreen"; // Import the new screen
import LandingPage1 from "./LandingPage1";
import AddListImagesScreen from "./AddListImagesScreen";
import ProductImagesManagerScreen from "./ProductImagesManagerScreen";
import { db } from "./firebase/config";
import PolicyScreen from "./PolicyScreen"; // Thêm dòng này ở phần import
import TermsScreen from "./TermsScreen"; // Thêm dòng này ở phần import
import { HelmetProvider } from "react-helmet-async";
import { Header as LandingHeader } from "./LandingPage1"; // Nhớ export Header từ LandingPage1
import AdminNewsGenerator from "./AdminNewsGenerator"; // Import AdminNewsGenerator
import MetaPixelPageTracker from "./components/MetaPixelPageTracker"; // Import Meta Pixel tracker
import { analytics } from "./firebase/config";
import { logEvent } from "firebase/analytics";
import LanguageSelector from "./components/LanguageSelector"; // Import LanguageSelector thay vì GoogleTranslate
import AdminDashboard from "./AdminDashboard"; // Import AdminDashboard
import AdminUserManagement from "./AdminUserManagement"; // Import AdminUserManagement
import AdminErrorManagement from "./AdminErrorManagement"; // Import AdminErrorManagement
import AdminBackupDashboard from "./components/AdminBackupDashboard"; // Import AdminBackupDashboard

import FeedbackForm from "./components/FeedbackForm"; // Import FeedbackForm
import { Outlet } from "react-router-dom"; // Import Outlet
import LandingPage2 from "./LandingPage2";
import SavedPostsPage from "./SavedPosts/SavedPostsPage";

// Layout component using MaintenanceGuard
const MaintenanceLayout = () => {
  return (
    <MaintenanceGuard>
      <Outlet />
    </MaintenanceGuard>
  );
};

const AppContent = () => {
  const {
    isAuthenticated,
    isLoading: authLoading,
    logout: authLogout,
  } = useAuth();

  // Get location first
  const location = useLocation();

  // Use AuthContext directly instead of separate state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false); // State to handle sidebar collapse
  const [expandedGroups, setExpandedGroups] = useState({
    posts: true,
    settings: true,
    admin: true,
  });
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  // Debug effect for initial load
  useEffect(() => {
    console.log("🚀 App.js mounted - Initial state:", {
      pathname: location.pathname,
      userId: localStorage.getItem("userId"),
      authMethod: localStorage.getItem("authMethod"),
      userAccessToken: !!localStorage.getItem("userAccessToken"),
      authLoading,
      isLoggedIn,
    });
  }, []);

  // Update isLoggedIn when authentication state changes
  useEffect(() => {
    const authStatus = isAuthenticated();
    console.log("🔍 App.js - Auth status changed:", {
      isAuthenticated: authStatus,
      authLoading,
      userId: localStorage.getItem("userId"),
      authMethod: localStorage.getItem("authMethod"),
      userAccessToken: !!localStorage.getItem("userAccessToken"),
      currentPath: location.pathname,
    });
    setIsLoggedIn(authStatus);
  }, [isAuthenticated, authLoading, location.pathname]);

  // Thêm tracking cho page views và bắt tham số quảng cáo Facebook
  useEffect(() => {
    try {
      // Bắt và lưu tham số quảng cáo Facebook
      captureFacebookAdParams();

      // Track page view
      if (analytics) {
        logEvent(analytics, "page_view", {
          page_path: location.pathname,
          page_title: document.title,
        });
      }
    } catch (error) {
      console.error("Analytics error:", error);
    }
  }, [location]);

  // Removed localStorage sync logic - now using AuthContext directly

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const userId = localStorage.getItem("userId");
    if (!userId) {
      // Nếu không có userId nhưng vẫn được đánh dấu là đã đăng nhập,
      // có thể đã xảy ra lỗi trạng thái - đăng xuất
      handleLogout();
      return;
    }

    // OPTIMIZED: Chỉ lắng nghe fanpages thay vì toàn bộ user data
    // Điều này giảm lượng tải xuống từ Firebase từ toàn bộ user profile
    // xuống chỉ fanpages, tiết kiệm ~90% bandwidth cho listener này
    const fanpagesRef = ref(db, `/users/${userId}/fanpages`);
    const unsubscribe = onValue(fanpagesRef, (snapshot) => {
      const fanpagesData = snapshot.val();
      if (fanpagesData) {
        localStorage.setItem("fanpages", JSON.stringify(fanpagesData));
      } else {
        localStorage.removeItem("fanpages");
      }
    });

    // IMPORTANT: Cleanup để tránh memory leak và realtime connections không đóng
    return () => {
      unsubscribe();
    };
  }, [isLoggedIn]);

  const handleLogout = async () => {
    // Track logout event
    try {
      if (analytics) {
        logEvent(analytics, "logout");
      }
    } catch (error) {
      console.error("Analytics error:", error);
    }

    // Use AuthContext logout which handles both Google and email users
    try {
      await authLogout();
      console.log("Đăng xuất thành công");
    } catch (error) {
      console.error("Lỗi khi đăng xuất:", error);
    }

    // Clean up additional app-specific data
    localStorage.removeItem("fanpages");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("adminAuthenticated");
    sessionStorage.removeItem("showMembershipWarning");
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  // First check if we're on public pages that should always show regardless of auth status
  if (
    location.pathname === "/policy" ||
    location.pathname === "/terms" ||
    location.pathname === "/news" ||
    location.pathname.startsWith("/news/") ||
    location.pathname === "/admin-news" ||
    location.pathname === "/admin-dashboard" ||
    location.pathname === "/admin-users" ||
    location.pathname === "/admin-errors"
  ) {
    if (location.pathname === "/policy") {
      return <PolicyScreen />;
    }
    if (location.pathname === "/terms") {
      return <TermsScreen />;
    }
    if (location.pathname === "/admin-news") {
      return <AdminNewsGenerator />;
    }
    if (location.pathname === "/admin-dashboard") {
      return <AdminDashboard />;
    }
    if (location.pathname === "/admin-users") {
      return <AdminUserManagement setActiveMenu={null} />;
    }
    if (location.pathname === "/admin-errors") {
      return <AdminErrorManagement />;
    }
  }

  // Handle landing page - only show if user is NOT logged in
  if (
    !isLoggedIn &&
    (location.pathname === "/" || location.pathname === "/landing-page")
  ) {
    return <LandingPage2 />;
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      {" "}
      {/* Added min-h-screen */}
      {isLoggedIn ? (
        <>
          {/* Overlay for mobile */}
          {isSidebarOpen && (
            <div
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {/* Mobile Header */}
          <header className="md:hidden fixed top-0 left-0 right-0 h-12 bg-gray-700 shadow-md z-30 flex items-center px-4">
            <button
              className="text-white mr-4"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <FiMenu className="text-xl" /> {/* Reduced icon size */}
            </button>
            <div className="flex items-center">
              <img
                src={require("./images/logo_AIContent.png")}
                alt="Logo"
                className="h-10" /* Increased logo height */
              />
              <span className="text-white font-bold text-xl ml-2">
                AIContent
              </span>
            </div>
          </header>

          {/* Sidebar Navigation */}
          <nav
            className={`bg-gray-800 flex flex-col fixed md:sticky top-0 h-screen z-20 transition-transform duration-300 ${
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            } md:translate-x-0 md:static md:mt-0 mt-12 ${
              isSidebarCollapsed ? "w-28" : "w-72"
            }`}
          >
            {/* Container cho toàn bộ nội dung có thể scroll */}
            <div className="flex flex-col h-full">
              {/* Phần có thể scroll */}
              <div className="flex-1 overflow-y-auto p-4 pb-24 md:pb-4 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                {/* Logo và header section */}
                <div className="flex-shrink-0">
                  <button
                    className="hidden md:block text-white mb-4"
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  >
                    <FiMenu className="text-xl" />
                  </button>
                </div>

                {/* Menu items */}
                <div className="flex-1">
                  <ul className="flex flex-col space-y-4 mb-8 md:mb-0">
                    {/* Post Creation Group */}
                    <div className="pb-2">
                      <button
                        onClick={() => toggleGroup("posts")}
                        className={`w-full text-white rounded flex items-center ${
                          isSidebarCollapsed
                            ? "justify-center px-3 py-4"
                            : "justify-between p-2"
                        } hover:bg-gray-700`}
                      >
                        <span className={isSidebarCollapsed ? "hidden" : ""}>
                          Bài viết
                        </span>
                        {!isSidebarCollapsed &&
                          (expandedGroups.posts ? (
                            <FiChevronDown />
                          ) : (
                            <FiChevronRight />
                          ))}
                      </button>
                      {expandedGroups.posts && (
                        <div className="ml-4 mt-2 space-y-2">
                          <li>
                            <Link
                              to="/auto-post"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/auto-post"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiSettings className="text-2xl" />
                              <span
                                className={
                                  isSidebarCollapsed ? "hidden" : "ml-2"
                                }
                              >
                                Đăng bài tự động
                              </span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/autopost-configs"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/autopost-configs"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiSave
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                }`}
                              >
                                Quản lý thiết lập
                              </span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/normal-post"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/normal-post"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiFileText
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                }`}
                              >
                                Tạo bài viết tự động
                              </span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/saved-posts"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/saved-posts"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiBookmark
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                }`}
                              >
                                Bài viết đã lưu
                              </span>
                            </Link>
                          </li>
                        </div>
                      )}
                    </div>

                    {/* Settings Group */}
                    <div className="py-2 border-t border-gray-700">
                      <button
                        onClick={() => toggleGroup("settings")}
                        className={`w-full text-white rounded flex items-center ${
                          isSidebarCollapsed
                            ? "justify-center px-3 py-4"
                            : "justify-between p-2"
                        } hover:bg-gray-700`}
                      >
                        <span className={isSidebarCollapsed ? "hidden" : ""}>
                          Cài đặt
                        </span>
                        {!isSidebarCollapsed &&
                          (expandedGroups.settings ? (
                            <FiChevronDown />
                          ) : (
                            <FiChevronRight />
                          ))}
                      </button>
                      {expandedGroups.settings && (
                        <div className="ml-4 mt-2 space-y-2">
                          <li>
                            <Link
                              to="/profile"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/profile"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiUser
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={isSidebarCollapsed ? "hidden" : ""}
                              >
                                Thiết lập thông tin
                              </span>
                            </Link>
                          </li>
                          <li>
                            <a
                              href="/landing-page#contact"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } hover:bg-gray-700`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiMessageCircle
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={isSidebarCollapsed ? "hidden" : ""}
                              >
                                Hỗ trợ
                              </span>
                            </a>
                          </li>
                          <li>
                            <Link
                              to="/policy"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/terms"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiFileText
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={isSidebarCollapsed ? "hidden" : ""}
                              >
                                Điều khoản dịch vụ
                              </span>
                            </Link>
                          </li>
                          <li>
                            <LanguageSelector
                              isSidebar={true}
                              isSidebarCollapsed={isSidebarCollapsed}
                            />
                          </li>
                          <li>
                            <button
                              onClick={() => {
                                handleLogout();
                                setIsSidebarOpen(false);
                              }}
                              className={`text-white rounded flex items-center w-full ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } hover:bg-gray-700`}
                            >
                              <FiLogOut
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={isSidebarCollapsed ? "hidden" : ""}
                              >
                                Đăng xuất
                              </span>
                            </button>
                          </li>
                        </div>
                      )}
                    </div>
                    {/* Feedback Button - Placed after Settings */}
                    <li className="py-2 border-t border-gray-700">
                      <button
                        onClick={() => setShowFeedbackForm(true)}
                        className={`text-white rounded flex items-center w-full ${
                          isSidebarCollapsed
                            ? "justify-center px-1 py-3"
                            : "justify-start p-2"
                        } hover:bg-gray-700`}
                      >
                        <FiMessageSquare
                          className={`text-2xl ${
                            !isSidebarCollapsed ? "mr-2" : ""
                          }`}
                        />
                        <span className={isSidebarCollapsed ? "hidden" : ""}>
                          Gửi Phản Hồi
                        </span>
                      </button>
                    </li>

                    {/* Admin Menu - chỉ hiển thị nếu là admin */}
                    {localStorage.getItem("isAdmin") === "true" && (
                      <div className="py-2 border-t border-gray-700">
                        <button
                          onClick={() => toggleGroup("admin")}
                          className={`w-full text-white rounded flex items-center ${
                            isSidebarCollapsed
                              ? "justify-center px-3 py-4"
                              : "justify-between p-2"
                          } hover:bg-gray-700`}
                        >
                          <div className="flex items-center">
                            <FiShield
                              className={`text-2xl ${
                                !isSidebarCollapsed ? "mr-2" : ""
                              }`}
                            />
                            <span
                              className={isSidebarCollapsed ? "hidden" : ""}
                            >
                              Quản trị hệ thống
                            </span>
                          </div>
                          {!isSidebarCollapsed &&
                            (expandedGroups.admin ? (
                              <FiChevronDown />
                            ) : (
                              <FiChevronRight />
                            ))}
                        </button>
                        {expandedGroups.admin && (
                          <div className="ml-4 mt-2 space-y-2">
                            <li>
                              <Link
                                to="/admin-dashboard"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin-dashboard"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiDatabase
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Bảng điều khiển
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/admin-users"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin-users"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiUsers
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Quản lý người dùng
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/admin-errors"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin-errors"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiActivity
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Quản lý lỗi
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/admin/backup"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin/backup"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiDatabase
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Backup & Dữ liệu
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/admin-news"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin-news"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiFileText
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Quản lý tin tức
                                </span>
                              </Link>
                            </li>
                          </div>
                        )}
                      </div>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </nav>

          {/* Main Content */}
          <div className="flex-1 p-0 md:p-4 md:mt-0 mt-10 min-h-screen">
            {" "}
            {/* Added min-h-screen */}
            <Routes>
              {/* Redirect root path to normal-post for logged in users */}
              <Route
                path="/"
                element={<Navigate to="/normal-post" replace />}
              />
              <Route
                path="/landing-page"
                element={<Navigate to="/normal-post" replace />}
              />
              <Route path="/admin-dashboard" element={<AdminDashboard />} />
              <Route path="/admin/backup" element={<AdminBackupDashboard />} />

              {/* Protected routes wrapped in MaintenanceLayout */}
              <Route element={<MaintenanceLayout />}>
                <Route path="/normal-post" element={<NormalPostGenerator />} />
                <Route path="/auto-post" element={<AutoPostGenerator />} />
                <Route
                  path="/autopost-configs"
                  element={<AutoPostConfigScreen />}
                />
                <Route path="/profile" element={<ProfileScreen />} />
                <Route
                  path="/product-create"
                  element={<ProductCreateScreen />}
                />
                <Route path="/products" element={<ProductsScreen />} />
                <Route
                  path="/add-photo-list"
                  element={<AddListImagesScreen />}
                />
                <Route
                  path="/product-images-manager"
                  element={<ProductImagesManagerScreen />}
                />
              </Route>

              <Route path="/saved-posts" element={<SavedPostsPage />} />

              <Route
                path="*"
                element={<Navigate to="/normal-post" replace />}
              />
            </Routes>
          </div>
          {/* Feedback Form Modal */}
          {showFeedbackForm && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000, // Ensure it's above other content
                padding: "20px", // Add padding for smaller screens
                overflowY: "auto", // Allow scrolling if form is tall
              }}
            >
              <FeedbackForm onClose={() => setShowFeedbackForm(false)} />
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-1 justify-center items-center">
          <Routes>
            <Route path="/landing-page" element={<LandingPage2 />} />
            <Route
              path="/login"
              element={
                <ProtectedRoute requireAuth={false}>
                  <Login />
                </ProtectedRoute>
              }
            />
            <Route path="/admin-news" element={<AdminNewsGenerator />} />
            <Route path="/" element={<LandingPage2 />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      )}
    </div>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <MaintenanceProvider>
        <MetaPixelPageTracker />
        <AppContent />
      </MaintenanceProvider>
    </HelmetProvider>
  );
};

export default App;
